import { useMemo, useState } from 'react';
import { legacyRoutes } from '@/lib/legacy';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  ClipboardCheck, Play, ExternalLink, AlertTriangle, FileSpreadsheet,
  PenLine, Info, Download, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { PageShell, UnderlineTabs, DateRangeFilter, EmptyState } from '@/shared/components';
import { usePersistedTab } from '@/shared/hooks/usePersistedFilters';
import { toCSV, downloadCSV } from '@/lib/csv';
import { cn } from '@/lib/utils';
import {
  useCertificateReview, useCertificateStats, useRegisterPreview, useRegisterPush, useAnnotateRejections,
} from '../hooks/useCertificateValidation';
import type {
  CertificateReviewRow, CertificatePoint, CertificateStatsResponse,
  RegisterPushPreview, RegisterPushResponse,
} from '../types';

// ---- date helpers (local browser date = Montreal for the reviewers) ----
const isoLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayIso = () => isoLocal(new Date());
const daysAgoIso = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoLocal(d);
};
const monthStartIso = () => {
  const d = new Date();
  return isoLocal(new Date(d.getFullYear(), d.getMonth(), 1));
};

type TabValue = 'review' | 'stats';
const VALID_TABS: readonly TabValue[] = ['review', 'stats'];

export default function CertificateValidationPage() {
  const { t } = useTranslation('intelligence');
  const [tab, setTab] = usePersistedTab<TabValue>('certificate-validation', VALID_TABS, 'review');

  return (
    <PageShell
      Icon={ClipboardCheck}
      subtitle={t('validation.subtitle', 'Certificate review, F-770 register and statistics')}
      tabs={(
        <UnderlineTabs<TabValue>
          tabs={[
            { value: 'review', label: t('validation.tabReview', 'Review') },
            { value: 'stats', label: t('validation.tabStats', 'Statistics') },
          ]}
          active={tab}
          onChange={setTab}
        />
      )}
    >
      {tab === 'review' ? <ReviewTab /> : <StatsTab />}
    </PageShell>
  );
}

// =====================================================================
// Review tab
// =====================================================================

function ReviewTab() {
  const { t } = useTranslation('intelligence');
  const [from, setFrom] = useState<string>(daysAgoIso(7));
  const [to, setTo] = useState<string>(todayIso());
  const [submitted, setSubmitted] = useState<{ from: string; to: string } | null>(null);
  const [greySelected, setGreySelected] = useState<Set<string>>(new Set());
  const [pushPreviewOpen, setPushPreviewOpen] = useState(false);
  const [annotateOpen, setAnnotateOpen] = useState(false);
  const [reportedRows, setReportedRows] = useState<CertificateReviewRow[]>([]);
  const [confirmedRows, setConfirmedRows] = useState<CertificateReviewRow[]>([]);
  const [pushPreview, setPushPreview] = useState<RegisterPushResponse | null>(null);
  // Certs annotated during THIS analysis. After annotating, the review refetch
  // flags them alreadyAnnotated — without this set they'd vanish from the
  // reject block and "Report to register" would go dead mid-flow (annotation
  // is step 1 of 2; certs annotated in a PREVIOUS session stay set aside).
  const [annotatedCerts, setAnnotatedCerts] = useState<Set<string>>(new Set());

  const query = useCertificateReview(submitted?.from, submitted?.to, !!submitted);
  const data = query.data;
  const registerPreview = useRegisterPreview();
  const registerPush = useRegisterPush();
  const annotate = useAnnotateRejections();

  const blocks = useMemo(() => {
    const rows = data?.rows ?? [];
    // A cert already in the F-770 register OR already carrying a "Rejeté" comment
    // annotation has been handled — set it aside so it never re-lists as actionable.
    // Exception: certs annotated in THIS session stay actionable until they are
    // reported to the register (the annotate → report sequence must not dead-end).
    const actionable = rows.filter((r) => !r.alreadyInRegister
      && (!r.alreadyAnnotated || annotatedCerts.has(r.cert)));
    return {
      sign: actionable.filter((r) => r.verdict === 'OK'),
      grey: actionable.filter((r) => r.verdict === 'ZONE-GRISE'),
      reject: actionable.filter((r) => r.verdict.startsWith('REJET')),
    };
  }, [data, annotatedCerts]);

  const toPush = useMemo(() => {
    const rej = blocks.reject;
    const grey = blocks.grey.filter((r) => greySelected.has(r.cert));
    return [...rej, ...grey];
  }, [blocks, greySelected]);
  const annotationRows = reportedRows.length > 0 ? reportedRows : toPush;

  const analyze = () => {
    setGreySelected(new Set());
    setReportedRows([]);
    setConfirmedRows([]);
    setPushPreview(null);
    setAnnotatedCerts(new Set());
    setSubmitted({ from, to });
  };

  const previewWrite = () => {
    const rows = [...toPush];
    setPushPreview(null);
    setConfirmedRows([]);
    registerPreview.mutate(rows.map((r) => r.cert), {
      onSuccess: (res) => {
        if (!res.confirmationToken || !res.preview) {
          toast.error(t('validation.pushPreviewFailed', 'Register preview failed.'));
          return;
        }
        const confirmed = new Set(res.preview.map((r) => r.cert));
        setConfirmedRows(rows.filter((r) => confirmed.has(r.cert)));
        setPushPreview(res);
        setPushPreviewOpen(true);
      },
      onError: () => toast.error(t('validation.pushPreviewFailed', 'Register preview failed.')),
    });
  };

  const doWrite = () => {
    const confirmationToken = pushPreview?.confirmationToken;
    if (!confirmationToken) return;
    registerPush.mutate({ certs: confirmedRows.map((r) => r.cert), confirmationToken }, {
      onSuccess: (res) => {
        const recorded = new Set([...(res.written ?? []), ...(res.skipped ?? [])]);
        setReportedRows(confirmedRows.filter((r) => recorded.has(r.cert)));
        setConfirmedRows([]);
        setPushPreview(null);
        setPushPreviewOpen(false);
        if ((res.missing?.length ?? 0) > 0) {
          toast.error(t('validation.pushMissing', '{{n}} certificate(s) missing after read-back', { n: res.missing?.length ?? 0 }));
        } else {
          toast.success(t('validation.pushDone', '{{w}} written, {{s}} already present', { w: res.written?.length ?? 0, s: res.skipped?.length ?? 0 }));
        }
      },
      onError: (err) => {
        const status = (err as { status?: number })?.status;
        if (status === 409) {
          setPushPreviewOpen(false);
          setConfirmedRows([]);
          setPushPreview(null);
        }
        toast.error(status === 403
          ? t('validation.pushDisabled', 'Writing to the register is disabled in this environment.')
          : status === 409
            ? t('validation.pushStale', 'Certificate data changed. Run the register preview again.')
            : status === 503
              ? t('validation.pushUnavailable', 'The F-770 register is unavailable — its server configuration is incomplete. Contact the technical team.')
              : t('validation.pushFailed', 'Register write failed.'));
      },
    });
  };

  const doAnnotate = () => {
    annotate.mutate(annotationRows.map((r) => ({ id: r.id, comment: 'Rejeté' })), {
      onSuccess: (res) => {
        setAnnotateOpen(false);
        // Remember what this session annotated: the invalidation refetch flags
        // them alreadyAnnotated, and they must stay actionable for the register
        // push. (A cert whose annotate actually FAILED refetches as
        // alreadyAnnotated=false and stays actionable on its own.)
        setAnnotatedCerts((prev) => new Set([...prev, ...annotationRows.map((r) => r.cert)]));
        if (res.failed > 0) toast.warning(t('validation.annotatePartial', '{{ok}}/{{total}} certificate(s) annotated', { ok: res.total - res.failed, total: res.total }));
        else {
          setReportedRows([]);
          toast.success(t('validation.annotateDone', '{{n}} certificate(s) annotated', { n: res.total }));
        }
      },
      onError: () => toast.error(t('validation.annotateFailed', 'Annotation failed.')),
    });
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        <DateRangeFilter from={from} to={to} onChange={(f, tv) => { if (f) setFrom(f); if (tv) setTo(tv); }} />
        <PresetButtons onPick={(f, tv) => { setFrom(f); setTo(tv); }} />
        <Button size="sm" onClick={analyze} disabled={query.isFetching}>
          {query.isFetching
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <Play className="h-4 w-4 mr-2" />}
          {t('validation.analyze', 'Analyze')}
        </Button>
      </div>

      {/* States */}
      {!submitted && (
        <EmptyState
          Icon={ClipboardCheck}
          heading={t('validation.chooseRangeTitle', 'Choose a date range')}
          description={t('validation.chooseRangeDesc', 'Pick a period and click Analyze to triage the unsigned certificates.')}
        />
      )}

      {submitted && query.isLoading && <ReviewSkeleton />}

      {submitted && query.isError && (
        <ErrorNote message={errText(query.error, t)} />
      )}

      {submitted && data && (
        <>
          <ReviewBanners data={data} t={t} />

          {data.rows.length === 0 ? (
            <EmptyState
              Icon={ClipboardCheck}
              heading={t('validation.emptyRangeTitle', 'No certificates to review')}
              description={t('validation.emptyRangeDesc', 'No unsigned certificate in this range. {{skipped}} out of scope, {{reg}} already in the register.', {
                skipped: data.counts.horsPerimetre, reg: data.counts.dejaAuRegistre,
              })}
            />
          ) : blocks.sign.length === data.rows.length ? (
            // "No rejection — good result" only when EVERY reviewed cert is a conforming
            // to-sign row. Rows set aside as already-handled (annotated / in register) stay
            // in data.rows, so basing this on the actionable sign count avoids the
            // contradiction of showing this note alongside a "N already rejected" banner.
            <div className="rounded-md border border-success/40 bg-success/5 px-4 py-3 text-sm text-success-foreground">
              {t('validation.allConform', '{{count}} certificate(s), no rejection — good result.', { count: blocks.sign.length })}
            </div>
          ) : null}

          <ResultBlock
            title={t('validation.blockSign', 'To sign')}
            hint={t('validation.blockSignHint', 'Conforming certificates')}
            tone="sign"
            rows={blocks.sign}
            t={t}
          />
          <ResultBlock
            title={t('validation.blockGrey', 'To decide')}
            hint={t('validation.blockGreyHint', 'TUR below threshold off the critical point — the reviewer decides')}
            tone="grey"
            rows={blocks.grey}
            t={t}
            selectable
            selected={greySelected}
            onToggle={(cert) => setGreySelected((prev) => {
              const next = new Set(prev);
              if (next.has(cert)) next.delete(cert); else next.add(cert);
              return next;
            })}
          />
          <ResultBlock
            title={t('validation.blockReject', 'To reject')}
            hint={t('validation.blockRejectHint', 'Rejection reason as it will appear in the register')}
            tone="reject"
            rows={blocks.reject}
            t={t}
            showReason
          />

          {/* Footer actions — each write is a simulation → confirmation → write. */}
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
            <Button
              variant="outline" size="sm"
              disabled={toPush.length === 0 || registerPreview.isPending}
              onClick={previewWrite}
            >
              {registerPreview.isPending
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <FileSpreadsheet className="h-4 w-4 mr-2" />}
              {t('validation.pushToRegister', 'Report to register')} ({toPush.length})
            </Button>
            <Button
              variant="outline" size="sm"
              disabled={annotationRows.length === 0}
              onClick={() => setAnnotateOpen(true)}
            >
              <PenLine className="h-4 w-4 mr-2" />
              {t('validation.annotate', 'Annotate rejections')} ({annotationRows.length})
            </Button>
          </div>

          <PushPreviewDialog
            open={pushPreviewOpen}
            onOpenChange={setPushPreviewOpen}
            rows={pushPreview?.preview ?? []}
            nonAtek={pushPreview?.warnings?.nonAtek ?? []}
            onWrite={doWrite}
            writing={registerPush.isPending}
            t={t}
          />

          <AlertDialog open={annotateOpen} onOpenChange={setAnnotateOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('validation.annotateConfirmTitle', 'Annotate rejections')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('validation.annotateConfirmDesc', 'A rejection note will be added to the comment of {{n}} certificate(s). This must be done before signature.', { n: annotationRows.length })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('validation.cancel', 'Cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={doAnnotate} disabled={annotate.isPending}>
                  {annotate.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t('validation.annotate', 'Annotate rejections')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}

// ---- review presentation pieces ----

const TONE_STYLES: Record<'sign' | 'grey' | 'reject', string> = {
  sign: 'border-success/40',
  grey: 'border-amber-400/50',
  reject: 'border-destructive/40',
};

function ResultBlock({
  title, hint, tone, rows, t, showReason, selectable, selected, onToggle,
}: {
  title: string;
  hint: string;
  tone: 'sign' | 'grey' | 'reject';
  rows: CertificateReviewRow[];
  t: TFunction<'intelligence'>;
  showReason?: boolean;
  selectable?: boolean;
  selected?: Set<string>;
  onToggle?: (cert: string) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <Card className={cn(TONE_STYLES[tone])}>
      <CardHeader className="py-3">
        <CardTitle className="text-sm flex items-center gap-2">
          {title}
          <Badge variant="secondary" className="h-5">{rows.length}</Badge>
          <span className="text-xs font-normal text-muted-foreground">{hint}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {selectable && <TableHead className="w-8" />}
                <TableHead>{t('validation.colCert', 'Certificate')}</TableHead>
                <TableHead>{t('validation.colProbe', 'Probe')}</TableHead>
                <TableHead>{t('validation.colUse', 'Declared use')}</TableHead>
                <TableHead className="text-center">{t('validation.colTur', 'TUR (3 points)')}</TableHead>
                {showReason && <TableHead>{t('validation.colReason', 'Reason')}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.cert}>
                  {selectable && (
                    <TableCell>
                      <Checkbox
                        checked={selected?.has(r.cert) ?? false}
                        onCheckedChange={() => onToggle?.(r.cert)}
                        aria-label={t('validation.selectForReject', 'Select for rejection')}
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-medium">
                    <a
                      href={legacyRoutes.calibrationRecord(r.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {r.cert}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    <div className="text-xs text-muted-foreground">{r.date}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{r.serial || '—'}</div>
                    <div className="text-muted-foreground">{[r.manufacturer, r.model].filter(Boolean).join(' ')}</div>
                  </TableCell>
                  <TableCell className="text-xs">{r.equipmentType || '—'}</TableCell>
                  <TableCell>
                    <TurCells points={r.points} critical={r.critical} floor={r.turFloor} unit={r.unit} />
                  </TableCell>
                  {showReason && (
                    <TableCell className="text-xs max-w-md">{r.reason}</TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

/** TUR value for each set-point: red when below the applicable floor, blue underline on the critical point. */
function TurCells({ points, critical, floor, unit }: {
  points: CertificatePoint[];
  critical: string[] | null;
  floor: number;
  unit: string;
}) {
  return (
    <div className="flex items-center justify-center gap-1">
      {points.map((p) => {
        const isCritical = critical ? critical.includes(p.setPoint) : true;
        return (
          <div key={p.setPoint} className="text-center min-w-[3.5rem]" title={`${p.setPoint}${unit}`}>
            <div className="text-[10px] text-muted-foreground">{p.setPoint}{unit}</div>
            <div
              className={cn(
                'text-xs tabular-nums rounded px-1',
                p.turFail && 'bg-destructive/15 text-destructive font-semibold',
                isCritical && 'underline decoration-2 decoration-blue-500 underline-offset-2',
              )}
            >
              {p.tur === null ? '—' : `${p.tur.toFixed(2)}`}
            </div>
          </div>
        );
      })}
      <div className="text-[10px] text-muted-foreground ml-1">≥{String(floor).replace('.', ',')}</div>
    </div>
  );
}

function ReviewBanners({ data, t }: { data: NonNullable<ReturnType<typeof useCertificateReview>['data']>; t: TFunction<'intelligence'> }) {
  const banners: React.ReactNode[] = [];
  for (const e of data.exceptions) {
    banners.push(
      <Banner key={`exc-${e.label}`} tone="info">
        {t('validation.bannerException', '{{count}} certificate(s) judged at the {{label}} exception threshold ({{floor}}:1 instead of 4:1)', {
          count: e.count, label: e.label, floor: String(e.turFloor).replace('.', ','),
        })}
      </Banner>,
    );
  }
  if (data.counts.dejaAuRegistre > 0) {
    banners.push(
      <Banner key="reg" tone="muted">
        {t('validation.bannerRegister', '{{count}} already in the register, set aside', { count: data.counts.dejaAuRegistre })}
      </Banner>,
    );
  }
  if (data.counts.dejaRejete > 0) {
    banners.push(
      <Banner key="annotated" tone="muted">
        {t('validation.bannerAnnotated', '{{count}} already annotated as rejected, set aside', { count: data.counts.dejaRejete })}
      </Banner>,
    );
  }
  if (data.counts.horsPerimetre > 0) {
    banners.push(
      <Banner key="oos" tone="muted">
        {t('validation.bannerOutOfScope', '{{count}} out of scope, not evaluated', { count: data.counts.horsPerimetre })}
      </Banner>,
    );
  }
  if (!data.registerAvailable) {
    banners.push(
      <Banner key="noreg" tone="warn">
        {t('validation.bannerNoRegister', 'The F-770 register is unavailable — already-recorded certificates cannot be flagged.')}
      </Banner>,
    );
  }
  if (banners.length === 0) return null;
  return <div className="space-y-2">{banners}</div>;
}

function Banner({ tone, children }: { tone: 'info' | 'muted' | 'warn'; children: React.ReactNode }) {
  const cls = {
    info: 'border-blue-400/40 bg-blue-500/5 text-blue-700 dark:text-blue-300',
    muted: 'border-border bg-muted/40 text-muted-foreground',
    warn: 'border-amber-400/50 bg-amber-500/5 text-amber-700 dark:text-amber-300',
  }[tone];
  return (
    <div className={cn('flex items-start gap-2 rounded-md border px-3 py-2 text-xs', cls)}>
      <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function PushPreviewDialog({ open, onOpenChange, rows, nonAtek, onWrite, writing, t }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  rows: RegisterPushPreview[];
  nonAtek: string[];
  onWrite: () => void;
  writing: boolean;
  t: TFunction<'intelligence'>;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('validation.pushPreviewTitle', 'Register entries — simulation')}</DialogTitle>
          <DialogDescription>
            {t('validation.pushPreviewDesc', 'Review these server-verified F-770-001-A entries. Writing is refused if the certificate data changes.')}
          </DialogDescription>
        </DialogHeader>
        {nonAtek.length > 0 && (
          <Banner tone="warn">
            {t('validation.pushCustomerWarning', 'Customer certificates require confirmation before writing:')} {nonAtek.join(', ')}
          </Banner>
        )}
        <div className="max-h-[50vh] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('validation.colCert', 'Certificate')}</TableHead>
                <TableHead>{t('validation.colDate', 'Date')}</TableHead>
                <TableHead>{t('validation.colTime', 'Time')}</TableHead>
                <TableHead>{t('validation.colProbe', 'Probe')}</TableHead>
                <TableHead>{t('validation.colLabTemperature', 'Lab °C')}</TableHead>
                <TableHead>{t('validation.colLabHumidity', 'Lab %RH')}</TableHead>
                <TableHead>{t('validation.colLabPressure', 'Lab hPa')}</TableHead>
                <TableHead>{t('validation.colRejecter', 'Reviewer')}</TableHead>
                <TableHead>{t('validation.colTechnician', 'Technician')}</TableHead>
                <TableHead>{t('validation.colReason', 'Reason')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.cert}>
                  <TableCell className="font-medium">{r.cert}</TableCell>
                  <TableCell className="text-xs">{r.date}</TableCell>
                  <TableCell className="text-xs">{r.time}</TableCell>
                  <TableCell className="text-xs">{r.serial || '—'}</TableCell>
                  <TableCell className="text-xs">{r.lab.t}</TableCell>
                  <TableCell className="text-xs">{r.lab.h}</TableCell>
                  <TableCell className="text-xs">{r.lab.p}</TableCell>
                  <TableCell className="text-xs">{r.rejecter}</TableCell>
                  <TableCell className="text-xs">{r.technician}</TableCell>
                  <TableCell className="text-xs">{r.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={writing}>
            {t('validation.close', 'Close')}
          </Button>
          <Button onClick={onWrite} disabled={writing || rows.length === 0}>
            {writing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('validation.write', 'Write to register')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 w-full" />)}
    </div>
  );
}

// =====================================================================
// Statistics tab
// =====================================================================

function StatsTab() {
  const { t } = useTranslation('intelligence');
  const [from, setFrom] = useState<string>(daysAgoIso(90));
  const [to, setTo] = useState<string>(todayIso());
  const query = useCertificateStats(from, to, true);
  const data = query.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        <DateRangeFilter from={from} to={to} onChange={(f, tv) => { if (f) setFrom(f); if (tv) setTo(tv); }} />
        <PresetButtons onPick={(f, tv) => { setFrom(f); setTo(tv); }} withQuarter />
        {query.isFetching && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </div>

      {query.isLoading && <Skeleton className="h-64 w-full" />}
      {query.isError && <ErrorNote message={errText(query.error, t)} />}
      {data && data.volume.emitted === 0 && (
        <EmptyState Icon={ClipboardCheck} heading={t('validation.statsEmptyTitle', 'No certificate in this range')} />
      )}
      {data && data.volume.emitted > 0 && <StatsContent data={data} t={t} />}
    </div>
  );
}

function StatsContent({ data, t }: { data: CertificateStatsResponse; t: TFunction<'intelligence'> }) {
  const familyLabel = (key: string) => t(`validation.family.${key}`, familyFallback(key));

  const recidivistCsv = () => {
    const csv = toCSV(data.recidivist, [
      { header: 'Serial', value: (r) => r.serial },
      { header: 'Family', value: (r) => familyFallback(r.family) },
      { header: 'Consecutive rejects', value: (r) => r.streak },
      { header: 'Rejects', value: (r) => r.rejects },
      { header: 'Total', value: (r) => r.total },
      { header: 'Last', value: (r) => r.last },
      { header: 'Still failing', value: (r) => (r.ongoing ? 'yes' : 'no') },
    ]);
    downloadCSV('recidivist-probes.csv', csv);
  };

  return (
    <div className="space-y-5">
      {/* Volume band */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        <Stat label={t('validation.statEmitted', 'Emitted')} value={data.volume.emitted} />
        <Stat label={t('validation.statInScope', 'In scope')} value={data.volume.inScope} />
        <Stat label={t('validation.statOutOfScope', 'Out of scope')} value={data.volume.outOfScope} />
        <Stat label={t('validation.statRejected', 'Rejected')} value={data.volume.rejected} />
        <Stat
          label={t('validation.statRate', 'Rejection rate')}
          value={ratioLabel(data.volume.rejected, data.volume.inScope, data.volume.rejectRate)}
        />
      </div>

      {!data.registerAvailable && (
        <Banner tone="warn">
          {t('validation.bannerNoRegisterStats', 'The F-770 register is unavailable — rejection figures fall back to the tool verdict and may differ from what was actually recorded.')}
        </Banner>
      )}

      {/* Median TUR per point — the most important metric */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">{t('validation.turByPoint', 'Median TUR per point')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('validation.colPoint', 'Point')}</TableHead>
                <TableHead className="text-right">{t('validation.colMedian', 'Median')}</TableHead>
                <TableHead className="text-right">{t('validation.colThreshold', 'Threshold')}</TableHead>
                <TableHead className="text-right">{t('validation.colMin', 'Min')}</TableHead>
                <TableHead className="text-right">{t('validation.colBelow', 'Below threshold')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.turByPoint.map((row) => {
                const tight = row.median !== null && row.median < row.floor * 1.25;
                const under = row.median !== null && row.median < row.floor;
                return (
                  <TableRow key={`${row.family}-${row.setPoint}`}>
                    <TableCell className="text-xs">{familyLabel(row.family)} @ {row.setPoint}</TableCell>
                    <TableCell className={cn(
                      'text-right tabular-nums',
                      under && 'text-destructive font-semibold',
                      !under && tight && 'text-amber-600 font-medium',
                    )}>{fmt(row.median)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{String(row.floor).replace('.', ',')}:1</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(row.min)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {ratioLabel(row.subFloor, row.total, row.total ? (row.subFloor / row.total) * 100 : null)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recidivist probes */}
      <Card>
        <CardHeader className="py-3 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm flex items-center gap-2">
            {t('validation.recidivist', 'Repeat-offender probes')}
            {data.recidivistOngoing > 0 && (
              <Badge variant="destructive" className="h-5">
                {t('validation.stillFailing', '{{n}} still failing', { n: data.recidivistOngoing })}
              </Badge>
            )}
          </CardTitle>
          {data.recidivist.length > 0 && (
            <Button variant="outline" size="sm" onClick={recidivistCsv}>
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {t('validation.export', 'Export')}
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {data.recidivist.length === 0 ? (
            <p className="px-4 py-3 text-xs text-muted-foreground">{t('validation.noRecidivist', 'No probe with 2+ consecutive rejections.')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('validation.colProbe', 'Probe')}</TableHead>
                  <TableHead>{t('validation.colFamily', 'Family')}</TableHead>
                  <TableHead className="text-right">{t('validation.colStreak', 'In a row')}</TableHead>
                  <TableHead className="text-right">{t('validation.colRejects', 'Rejects')}</TableHead>
                  <TableHead>{t('validation.colLast', 'Last')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recidivist.map((r) => (
                  <TableRow key={r.serial}>
                    <TableCell className="font-medium text-xs">{r.serial}</TableCell>
                    <TableCell className="text-xs">{familyLabel(r.family)}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.streak}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.rejects}/{r.total}</TableCell>
                    <TableCell className="text-xs">
                      {r.last}
                      {r.ongoing && (
                        <span className="ml-1 inline-flex items-center text-destructive">
                          <AlertTriangle className="h-3 w-3" />
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* By family / by model */}
      <div className="grid gap-4 md:grid-cols-2">
        <GroupTable title={t('validation.byFamily', 'Rejections by family')} rows={data.byFamily} label={familyLabel} t={t} />
        <GroupTable title={t('validation.byModel', 'Rejections by model')} rows={data.byModel} label={(k) => k} t={t} />
      </div>

      {/* Drift */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">{t('validation.drift', 'As-received drift (|measured − reference|)')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('validation.colPoint', 'Point')}</TableHead>
                <TableHead className="text-right">n</TableHead>
                <TableHead className="text-right">{t('validation.colMean', 'Mean')}</TableHead>
                <TableHead className="text-right">{t('validation.colMedian', 'Median')}</TableHead>
                <TableHead className="text-right">p90</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.drift.map((d) => (
                <TableRow key={`${d.family}-${d.setPoint}`}>
                  <TableCell className="text-xs">{familyLabel(d.family)} @ {d.setPoint}</TableCell>
                  <TableCell className="text-right tabular-nums">{d.n}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(d.mean)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(d.median)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(d.p90)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reasons + monthly + reprises */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm">{t('validation.reasons', 'Rejection reasons')}</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <Line label={t('validation.reasonTur', 'TUR below threshold')} value={data.reasons.tur} />
            <Line label={t('validation.reasonAberrant', 'Aberrant data')} value={data.reasons.aberrant} />
            <Line label={t('validation.reasonOther', 'Other')} value={data.reasons.other} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm">{t('validation.byMonth', 'Rejection rate by month')}</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t('validation.colMonth', 'Month')}</TableHead>
                <TableHead className="text-right">{t('validation.colRate', 'Rate')}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.byMonth.map((m) => (
                  <TableRow key={m.month}>
                    <TableCell className="text-xs">{m.month}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{ratioLabel(m.rejected, m.emitted, m.rate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm">{t('validation.reprises', 'Re-calibrations (cost of rejection)')}</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <Line label={t('validation.repriseRejected', 'Rejections to redo')} value={data.reprises.rejected} />
            <Line label={t('validation.repriseDistinct', 'Distinct probes')} value={data.reprises.distinctProbes} />
            <Line label={t('validation.repriseRepeated', 'Probes back more than once')} value={data.reprises.repeatedProbes} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function GroupTable({ title, rows, label, t }: {
  title: string;
  rows: CertificateStatsResponse['byFamily'];
  label: (k: string) => string;
  t: TFunction<'intelligence'>;
}) {
  return (
    <Card>
      <CardHeader className="py-3"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t('validation.colName', 'Name')}</TableHead>
            <TableHead className="text-right">{t('validation.colEmitted', 'Emitted')}</TableHead>
            <TableHead className="text-right">{t('validation.colRejected', 'Rejected')}</TableHead>
            <TableHead className="text-right">{t('validation.colRate', 'Rate')}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((g) => (
              <TableRow key={g.key}>
                <TableCell className="text-xs">{label(g.key)}</TableCell>
                <TableCell className="text-right tabular-nums">{g.emitted}</TableCell>
                <TableCell className="text-right tabular-nums">{g.rejected}</TableCell>
                <TableCell className="text-right tabular-nums text-xs">{ratioLabel(g.rejected, g.emitted, g.rate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Shared bits
// =====================================================================

function PresetButtons({ onPick, withQuarter }: { onPick: (from: string, to: string) => void; withQuarter?: boolean }) {
  const { t } = useTranslation('intelligence');
  const today = todayIso();
  return (
    <div className="flex items-center gap-1">
      <Preset label={t('validation.preset7', '7d')} onClick={() => onPick(daysAgoIso(7), today)} />
      <Preset label={t('validation.preset30', '30d')} onClick={() => onPick(daysAgoIso(30), today)} />
      <Preset label={t('validation.presetMonth', 'This month')} onClick={() => onPick(monthStartIso(), today)} />
      {withQuarter && <Preset label={t('validation.preset90', '90d')} onClick={() => onPick(daysAgoIso(90), today)} />}
    </div>
  );
}

function Preset({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button variant="ghost" size="sm" className="h-8 text-xs px-2" onClick={onClick}>{label}</Button>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-lg font-semibold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

function Line({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums font-medium">{value}</span>
    </div>
  );
}

function ErrorNote({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

// ---- helpers ----
const fmt = (n: number | null) => (n === null || n === undefined ? '—' : String(n));

/** "z% (x/y)" but "x/y" when the denominator is below 20 (spec §6.4). */
function ratioLabel(numerator: number, denominator: number, rate: number | null): string {
  if (denominator < 20) return `${numerator}/${denominator}`;
  const pct = rate === null || rate === undefined ? Math.round((numerator / denominator) * 100) : rate;
  return `${pct}% (${numerator}/${denominator})`;
}

function familyFallback(key: string): string {
  switch (key) {
    case 'TH_TEMP': return 'TH temperature';
    case 'TH_RH': return 'TH humidity';
    case 'DIGITAL_1W': return 'Digital 1-Wire';
    default: return key;
  }
}

function errText(error: unknown, t: TFunction<'intelligence'>): string {
  const status = (error as { status?: number })?.status;
  if (status === 403) return t('validation.err403', 'You do not have the calibration permission required to view this page.');
  const msg = (error as { message?: string })?.message;
  return msg || t('validation.errGeneric', 'Failed to load certificate data.');
}
